import { z } from "zod";
import { money, trimmed, uuid } from "./common";

/**
 * Услуги и аренда (`addon.services`, ТЗ 09 §9.12): услуга — позиция меню типа «service» с настройками тарификации.
 * Штучные (кальян, инвентарь) продаются количеством; почасовые (комната, караоке) — сессией с серверным таймером (BR-201):
 * минуты → шаг `stepMin` с округлением (BR-199), минимум `minDurationMin`, ступени («первый час 1000, дальше 800»),
 * цена по расписанию (день недели × время начала). Комиссия сотруднику — снимок правила на момент продажи (BR-182).
 */
export const SERVICE_BILLING = ["unit", "hourly"] as const;
export const SERVICE_ROUNDING = ["up", "nearest", "down"] as const;
export const COMMISSION_TYPES = ["none", "fixed", "percent", "mixed"] as const;
export type ServiceRounding = (typeof SERVICE_ROUNDING)[number];
export type CommissionType = (typeof COMMISSION_TYPES)[number];

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, { error: "Время ЧЧ:ММ" });
/** Ступень: с минуты `fromMin` действует `hourlyPrice` (первый час — базовая цена) */
const tier = z.strictObject({ fromMin: z.number().int().min(1).max(24 * 60), hourlyPrice: money });
/** Цена по расписанию: дни (1 = пн … 7 = вс) и время начала сессии `from`–`to` */
const slot = z.strictObject({ days: z.array(z.number().int().min(1).max(7)).min(1).max(7), from: hhmm, to: hhmm, hourlyPrice: money });

export const serviceSettingsSchema = z
  .strictObject({
    billing: z.enum(SERVICE_BILLING),
    hourlyPrice: money.default(0),
    stepMin: z.number().int().min(1).max(240).default(30),
    rounding: z.enum(SERVICE_ROUNDING).default("up"),
    minDurationMin: z.number().int().min(0).max(24 * 60).default(0),
    tiers: z.array(tier).max(10).default([]),
    schedule: z.array(slot).max(20).default([]),
    commissionType: z.enum(COMMISSION_TYPES).default("none"),
    commissionFixed: money.default(0),
    commissionPercent: z.number().min(0).max(100).default(0),
  })
  .refine((s) => s.billing !== "hourly" || s.hourlyPrice > 0 || s.schedule.length > 0, { error: "Укажите цену за час", path: ["hourlyPrice"] });
export type ServiceSettings = z.output<typeof serviceSettingsSchema>;
export type ServiceSettingsInput = z.input<typeof serviceSettingsSchema>;

const roomFields = { name: trimmed(1, 80), tableId: uuid.nullable(), minOrderAmount: money, depositAmount: money, isActive: z.boolean() };
export const serviceRoomSchema = z.strictObject({
  ...roomFields,
  tableId: roomFields.tableId.optional(),
  minOrderAmount: money.default(0),
  depositAmount: money.default(0),
  isActive: z.boolean().default(true),
});
/** Без значений по умолчанию: не переданное поле не меняется (у `.partial()` умолчания бы сработали) */
export const serviceRoomUpdateSchema = z.strictObject(roomFields).partial().extend({ version: z.number().int().min(1) });

export const serviceSessionStartSchema = z.strictObject({
  menuItemId: uuid,
  /** Заказ, куда пойдёт услуга; без него — новый заказ (в зал, к столу комнаты / `tableId`) */
  orderId: uuid.nullable().optional(),
  tableId: uuid.nullable().optional(),
  roomId: uuid.nullable().optional(),
  /** Исполнитель — ему комиссия */
  employeeId: uuid.nullable().optional(),
  /** Штучная услуга: количество (продаётся сразу) */
  quantity: z.number().positive().max(1000).optional(),
  /** Оплаченное/обещанное время — для предупреждения об окончании */
  plannedMinutes: z.number().int().min(1).max(24 * 60).nullable().optional(),
});
export type ServiceSessionStartInput = z.input<typeof serviceSessionStartSchema>;
export const serviceSessionsQuery = z.object({ status: z.enum(["active", "stopped", "all"]).default("active") });

/** Минуты к оплате: минимум, затем шаг с округлением (AC-SRV-01: 130 мин, шаг 30, вверх → 150). */
export function billableMinutes(minutes: number, s: Pick<ServiceSettings, "stepMin" | "rounding" | "minDurationMin">): number {
  const m = Math.max(0, minutes, s.minDurationMin);
  const step = Math.max(1, s.stepMin);
  const k = m / step;
  const steps = s.rounding === "up" ? Math.ceil(k - 1e-9) : s.rounding === "down" ? Math.floor(k + 1e-9) : Math.round(k);
  return Math.max(0, steps) * step;
}

/**
 * Стоимость почасовой услуги (minor): по минутам к оплате, ступени — по своей цене часа с минуты `fromMin`.
 * `baseHourly` — цена часа на момент начала (из расписания или базовая).
 */
export function hourlyAmount(billedMin: number, baseHourly: number, tiers: { fromMin: number; hourlyPrice: number }[] = []): number {
  const marks = [{ fromMin: 0, hourlyPrice: baseHourly }, ...[...tiers].sort((a, b) => a.fromMin - b.fromMin)];
  let total = 0;
  for (let i = 0; i < marks.length; i++) {
    const from = marks[i]!.fromMin, to = Math.min(billedMin, marks[i + 1]?.fromMin ?? Infinity);
    if (to > from) total += ((to - from) / 60) * marks[i]!.hourlyPrice;
  }
  return Math.round(total);
}

/** Цена часа по расписанию для начала сессии (день недели 1–7 и «ЧЧ:ММ» местного времени), иначе базовая. */
export function scheduledHourly(s: Pick<ServiceSettings, "hourlyPrice" | "schedule">, isoDow: number, hm: string): number {
  const hit = s.schedule.find((x) => x.days.includes(isoDow) && (x.from <= x.to ? hm >= x.from && hm < x.to : hm >= x.from || hm < x.to));
  return hit ? hit.hourlyPrice : s.hourlyPrice;
}

/** Комиссия исполнителю с суммы строки (BR-182). */
export function serviceCommission(amount: number, s: Pick<ServiceSettings, "commissionType" | "commissionFixed" | "commissionPercent">): number {
  if (s.commissionType === "none") return 0;
  const fixed = s.commissionType === "fixed" || s.commissionType === "mixed" ? s.commissionFixed : 0;
  const pct = s.commissionType === "percent" || s.commissionType === "mixed" ? Math.round((amount * s.commissionPercent) / 100) : 0;
  return fixed + pct;
}
